import { Dialog, DialogClose, DialogTitle } from "@suse-o11y/dialog";
import { IconCaretDownSmall, IconClose, IconMainMenuMonitors } from "@suse-o11y/icons";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";
import { Tabs } from "~/components/tabs";
import { TabsContent } from "~/components/tabs/content";
import { TabsList } from "~/components/tabs/list";
import { TabsTrigger } from "~/components/tabs/trigger";
import { Permission } from "~/models/dto";

import { Api } from "../../common/openapi";
import { usePermissions } from "../../common/use-permissions";
import { MonitorContextMenu } from "../../monitors/MonitorContextMenu";
import { queryMonitorStatus, useInvalidateMonitorStatusQuery, useUpdateMonitorStatus } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { ErrorMessage } from "../_notification-error";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";

import { DefinitionTabContent } from "./DefinitionTabContent";
import { IssuesTabContent } from "./IssueTabContent";
import { UnmappedComponentsTabContent } from "./UnmappedComponentsTabContent";

const TAB_YAML = "yaml";
const TAB_ERRORS = "errors";
const TAB_WARNINGS = "warnings";
const TAB_UNMAPPED = "unmapped";

type MonitorPreviewTab = typeof TAB_YAML | typeof TAB_ERRORS | typeof TAB_WARNINGS | typeof TAB_UNMAPPED;

export interface MonitorPreviewDrawerProps {
	title: string;
	monitorId: number;
	initialTab: MonitorPreviewTab;
}

export const MonitorPreviewDrawer: React.FC<MonitorPreviewDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide">
		<MonitorPreviewDrawerContent {...props} />
	</Dialog>
);

const MonitorPreviewDrawerContent: React.FC<MonitorPreviewDrawerProps> = ({ title, monitorId, initialTab }) => {
	const permissions = usePermissions();
	const hasCreateMonitorsPermission = permissions?.hasSystemPermission(Permission.CreateMonitors);
	const hasUpdateMonitorsPermission = permissions?.hasSystemPermission(Permission.UpdateMonitors);

	const { data, error, isLoading, refetch } = useQuery(queryMonitorStatus({ monitorIdOrUrn: monitorId }));

	const invalidateMonitorStatus = useInvalidateMonitorStatusQuery();
	const { isLoading: isUpdating, mutate: updateMonitorStatus } = useUpdateMonitorStatus({
		onSuccess: (result) => {
			notification.success({
				message: `Monitor ${result.name} ${result.status === Api.MonitorStatusValue.ENABLED ? "enabled" : "disabled"}.`,
			});

			return invalidateMonitorStatus();
		},
		onError: (err) => {
			notification.error({
				duration: DONT_AUTO_DISMISS,
				message: <ErrorMessage error={err}>Can't change status for monitor {data?.monitor.name}.</ErrorMessage>,
			});
		},
	});

	const unmatchedCheckStates = data == null ? undefined : (data.topologyMatchResult?.unmatchedCheckStates ?? []);
	const errors = data == null ? undefined : (data.errors ?? []);
	const messageLevelErrors = errors?.filter(({ level }) => level === Api.MessageLevel.ERROR);
	const messageLevelWarns = errors?.filter(({ level }) => level === Api.MessageLevel.WARN);

	return (
		<>
			<header className="drawer-header">
				<IconMainMenuMonitors className="size-5 text-grey-800" />

				<DialogTitle className="drawer-title">{title}</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			{error == null ? (
				<Tabs className="drawer-body gap-4" defaultValue={initialTab}>
					<TabsList className="justify-start">
						<TabsTrigger value={TAB_YAML}>Monitor definition</TabsTrigger>

						{messageLevelErrors?.length !== 0 && (
							<TabsTrigger className="disabled:skeleton" disabled={messageLevelErrors == null} value={TAB_ERRORS}>
								Errors ({messageLevelErrors?.length ?? 0})
							</TabsTrigger>
						)}

						{messageLevelWarns?.length !== 0 && (
							<TabsTrigger className="disabled:skeleton" disabled={messageLevelWarns == null} value={TAB_WARNINGS}>
								Warnings ({messageLevelWarns?.length ?? 0})
							</TabsTrigger>
						)}

						{unmatchedCheckStates?.length !== 0 && (
							<TabsTrigger className="disabled:skeleton" disabled={unmatchedCheckStates == null} value={TAB_UNMAPPED}>
								Unmapped identifiers ({unmatchedCheckStates?.length ?? 0})
							</TabsTrigger>
						)}
					</TabsList>

					<TabsContent className="h-full" value={TAB_YAML}>
						<DefinitionTabContent className="h-full" monitorId={monitorId} title={title} />
					</TabsContent>

					<TabsContent className="h-full overflow-y-auto" value={TAB_ERRORS}>
						<IssuesTabContent errors={messageLevelErrors ?? []} level={Api.MessageLevel.ERROR} />
					</TabsContent>

					<TabsContent className="h-full overflow-y-auto" value={TAB_WARNINGS}>
						<IssuesTabContent errors={messageLevelWarns ?? []} level={Api.MessageLevel.WARN} />
					</TabsContent>

					<TabsContent className="h-full overflow-y-auto" value={TAB_UNMAPPED}>
						<UnmappedComponentsTabContent unmatchedCheckStates={unmatchedCheckStates ?? []} />
					</TabsContent>
				</Tabs>
			) : (
				<GenericErrorMessage
					className="drawer-body"
					error={error}
					highlightText="Failed"
					onRetry={() => refetch()}
					text={`Failed to load monitor status for '${title}`}
				/>
			)}

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<div className="join join-space-0">
					<TooltipBoundary hide={hasUpdateMonitorsPermission}>
						<TooltipTrigger
							aria-busy={isUpdating}
							aria-disabled={!hasUpdateMonitorsPermission || error != null}
							className="button button-primary join-item state-loading:skeleton uppercase"
							data-state={isLoading ? "loading" : undefined}
							onClick={() => {
								if (hasUpdateMonitorsPermission && data && !isUpdating) {
									updateMonitorStatus({
										monitorId: data.monitor.id,
										status:
											data.monitor.status === Api.MonitorStatusValue.ENABLED
												? Api.MonitorStatusValue.DISABLED
												: Api.MonitorStatusValue.ENABLED,
									});
								}
							}}
						>
							{data?.monitor.status === Api.MonitorStatusValue.ENABLED ? "Disable" : "Enable"} monitor
						</TooltipTrigger>

						<Tooltip className="tooltip/tooltip">You don't have permission to update monitors.</Tooltip>
					</TooltipBoundary>

					{(hasCreateMonitorsPermission || hasUpdateMonitorsPermission) && (
						<DropdownMenu>
							<DropdownMenuTrigger
								aria-busy={isUpdating}
								aria-label="Show more actions"
								asChild={false}
								className="join-item button button-primary button-square state-loading:skeleton"
								data-state={isLoading ? "loading" : undefined}
								disabled={error != null}
								type="button"
							>
								<IconCaretDownSmall width="7" />
							</DropdownMenuTrigger>

							{data && (
								<DropdownMenuContent align="end" className="z-drawer!">
									<MonitorContextMenu monitor={data.monitor} />
								</DropdownMenuContent>
							)}
						</DropdownMenu>
					)}
				</div>
			</footer>
		</>
	);
};
